import { ThemeProvider, createTheme } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import type {} from '@mui/x-date-pickers/themeAugmentation'
import React, { PropsWithChildren } from 'react'
import { Provider as ReduxProvider, connect } from 'react-redux'
import { BrowserRouter as RouterProvider } from 'react-router-dom'
import { configureStore } from 'states'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import State from 'states/types'
import { mapOperationsToDispatchProps } from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
  }
}

type OwnProps = PropsWithChildren

type AppProps = StateProps & DispatchProps & OwnProps

const NivosMuiThemeProviderFC: React.FC<AppProps> = props => {
  const { theme, children } = props
  const nivosAdaptiveTheme = createTheme({
    typography: {
      allVariants: {
        fontFamily: 'Montserrat'
      }
    },
    palette: {
      primary: {
        main: theme.secondaryColor,
        dark: theme.tertiaryColor,
        light: theme.quaternaryColor,
        contrastText: theme.primaryColor
      },
      secondary: {
        main: theme.secondaryColor,
        dark: theme.tertiaryColor,
        light: theme.quaternaryColor,
        contrastText: theme.primaryColor
      }
    },
    components: {
      MuiPaginationItem: {
        styleOverrides: {
          root: {
            color: theme.secondaryColor
          }
        }
      },
      MuiChip: {
        defaultProps: {
          color: 'primary'
        },
        styleOverrides: {
          filledPrimary: {
            backgroundColor:
              theme.custom != null ? theme.custom : theme.quaternaryColor,
            color: theme.secondaryColor
          },
          colorWarning: {
            backgroundColor: '#FDB1A0',
            color: 'black'
          },
          root: {
            fontFamily: 'Montserrat'
          }
        }
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: theme.secondaryColor
          }
        }
      },
      MuiMenu: {
        styleOverrides: {
          list: {
            color: theme.secondaryColor
          }
        }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            borderColor: '#d4d2d2'
          },
          notchedOutline: {
            borderColor: '#d4d2d2'
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            borderRadius: '12px'
          }
        }
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            color: theme.secondaryColor
          }
        }
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: `${theme.secondaryColor}4c`
          }
        }
      },
      MuiImageListItemBar: {
        styleOverrides: {
          root: {
            overflow: 'hidden',
            alignItems: 'flex-start',
            background: 'rgba(0, 0, 0, 0.70)'
          },
          subtitle: {
            whiteSpace: 'normal'
          },
          actionIcon: {
            paddingTop: '10px'
          }
        }
      },
      MuiMenuItem: {
        defaultProps: {
          color: 'primary'
        }
      },
      MuiTypography: {
        defaultProps: {
          color: 'primary'
        },
        styleOverrides: {
          root: {
            whiteSpace: 'pre-line'
          }
        }
      },
      MuiIconButton: {
        defaultProps: {
          color: 'primary'
        }
      },
      MuiIcon: {
        defaultProps: {
          color: 'primary'
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: theme.primaryColor
          }
        }
      },
      MuiClock: {
        styleOverrides: {
          root: {
            cursor: 'pointer',
            '.MuiButtonBase-root': {
              backgroundColor: theme.primaryColor + ' !important'
            },
            '.MuiButtonBase-root:hover': {
              '.MuiTypography-root': {
                color: theme.primaryColor + ' !important'
              },
              backgroundColor: theme.secondaryColor + ' !important'
            },
            '.MuiButtonBase-root:active': {
              '.MuiTypography-root': {
                color: theme.primaryColor + ' !important'
              },
              backgroundColor: theme.secondaryColor + ' !important'
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: theme.secondaryColor
          }
        }
      },
      MuiCalendarPicker: {
        styleOverrides: {
          root: {
            '.MuiPickersCalendarHeader-label': {
              color: theme.secondaryColor
            },
            '.MuiDayPicker-weekDayLabel': {
              color: theme.secondaryColor
            },
            '.MuiSvgIcon-root': {
              color: theme.secondaryColor
            },
            '.MuiPickersDay-today': {
              border: '1px solid ' + theme.tertiaryColor + ' !important'
            }
          }
        }
      },
      MuiYearPicker: {
        styleOverrides: {
          root: {
            backgroundColor: theme.quaternaryColor,
            color: theme.secondaryColor
          }
        }
      },
      MuiPickersDay: {
        styleOverrides: {
          root: {
            color: theme.secondaryColor,
            backgroundColor: theme.quaternaryColor
          }
        }
      }
    }
  })
  return <ThemeProvider theme={nivosAdaptiveTheme}>{children}</ThemeProvider>
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations
})

const NivosMuiThemeProvider: any = connect(
  mapStateToProps,
  mapDispatchToProps
)(NivosMuiThemeProviderFC)

const NivosProviders: React.FC<PropsWithChildren> = props => {
  const store = configureStore()
  return (
    <ReduxProvider store={store}>
      <NivosMuiThemeProvider>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <RouterProvider>{props.children}</RouterProvider>
        </LocalizationProvider>
      </NivosMuiThemeProvider>
    </ReduxProvider>
  )
}

export default NivosProviders
